import { api } from '../lib/api';
import type { ChargeType, CsvImportResult, CustomerCatalogItem, InvoiceFrequency, MasterCatalogItem } from '../types/api';

export async function listMasterItems(): Promise<MasterCatalogItem[]> {
  const { data } = await api.get<MasterCatalogItem[]>('/catalog/master-items');
  return data;
}

export async function importMasterItems(file: File): Promise<CsvImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<CsvImportResult>('/catalog/master-items/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export interface CreateMasterItemInput {
  name: string;
  description?: string;
  imageUrl?: string;
  category?: string;
  defaultCostMonthly?: number;
  defaultCostTotal?: number;
}

export async function createMasterItem(input: CreateMasterItemInput): Promise<MasterCatalogItem> {
  const { data } = await api.post<MasterCatalogItem>('/catalog/master-items', input);
  return data;
}

export async function updateMasterItem(
  itemId: string,
  input: Partial<CreateMasterItemInput>,
): Promise<MasterCatalogItem> {
  const { data } = await api.patch<MasterCatalogItem>(`/catalog/master-items/${itemId}`, input);
  return data;
}

export async function addMasterItemPhoto(itemId: string, file: File): Promise<MasterCatalogItem> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<MasterCatalogItem>(`/catalog/master-items/${itemId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function removeMasterItemPhoto(itemId: string, photoId: string): Promise<MasterCatalogItem> {
  const { data } = await api.delete<MasterCatalogItem>(`/catalog/master-items/${itemId}/photos/${photoId}`);
  return data;
}

export async function listCustomerCatalog(orgId: string): Promise<CustomerCatalogItem[]> {
  const { data } = await api.get<CustomerCatalogItem[]>(`/catalog/customer-organisations/${orgId}/items`);
  return data;
}

export async function importCustomerItems(orgId: string, file: File): Promise<CsvImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<CsvImportResult>(`/catalog/customer-organisations/${orgId}/items/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export interface CustomerItemInput {
  masterCatalogItemId?: string;
  siteId?: string;
  name: string;
  description?: string;
  imageUrl?: string;
  category?: string;
  price: number;
  chargeTypeOverride?: ChargeType;
  managedTermMonths?: number;
  managedFrequency?: InvoiceFrequency;
  visible?: boolean;
}

export async function createCustomerItem(orgId: string, input: CustomerItemInput): Promise<CustomerCatalogItem> {
  const { data } = await api.post<CustomerCatalogItem>(`/catalog/customer-organisations/${orgId}/items`, input);
  return data;
}

export async function updateCustomerItem(
  itemId: string,
  input: Partial<Omit<CustomerItemInput, 'masterCatalogItemId'>>,
): Promise<CustomerCatalogItem> {
  const { data } = await api.patch<CustomerCatalogItem>(`/catalog/items/${itemId}`, input);
  return data;
}

// The "shop window" - items visible to a specific site, respecting local
// customisation (org-wide items plus that site's own).
export async function listSiteCatalog(siteId: string): Promise<CustomerCatalogItem[]> {
  const { data } = await api.get<CustomerCatalogItem[]>(`/catalog/sites/${siteId}/items`);
  return data;
}

export async function addCatalogItemPhoto(itemId: string, file: File): Promise<CustomerCatalogItem> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<CustomerCatalogItem>(`/catalog/items/${itemId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function removeCatalogItemPhoto(itemId: string, photoId: string): Promise<CustomerCatalogItem> {
  const { data } = await api.delete<CustomerCatalogItem>(`/catalog/items/${itemId}/photos/${photoId}`);
  return data;
}
